/* BootyLift drawer backed by Shopify's real cart endpoints. */
(() => {
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  class BootyliftCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        document.addEventListener('click', this.onTrigger);
        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
        this.updateReservation(this.cart.item_count || 0);
        return;
      }
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.root = window.Shopify?.routes?.root || '/';
      this.cart = JSON.parse(this.querySelector('[data-cart-initial]').textContent || '{}');
      this.recommendationProduct = JSON.parse(this.querySelector('[data-recommendation-product]').textContent || 'null');
      try { this.linePhotos = JSON.parse(this.querySelector('[data-cart-line-photos]')?.textContent || '{}'); } catch { this.linePhotos = {}; }
      this.recommendationCards = JSON.parse(this.querySelector('[data-recommendation-cards]').textContent || '[]');
      // New colours lead the upsell list.
      const washOrder = ['Brown', 'White', 'Light Blue', 'Dark Blue', 'Black', 'Gray', 'White Leopard'];
      this.recommendationCards.sort((a, b) => (washOrder.indexOf(a.color) + 1 || 99) - (washOrder.indexOf(b.color) + 1 || 99));
      this.priorityProduct = JSON.parse(this.querySelector('[data-priority-product]').textContent || 'null');
      this.onTrigger = event => {
        if (!event.target.closest('[data-cart-open]')) return;
        event.preventDefault(); this.open();
      };
      this.onCartUpdate = async event => {
        const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
        try {
          const result = await event.promise;
          if (result?.detail?.didError || !this.isConnected) return;
          if (event.action === 'add') {
            await this.prefetch();
            if (sourceModal?.open) sourceModal.addEventListener('close', () => this.open(), {once: true});
            else await this.open();
          } else await this.onPageShow();
        } catch { /* The source form reports failed changes. */ }
      };
      this.onPageShow = async () => {
        this.querySelector('.tc-cart-checkout')?.classList.remove('is-loading');
        if (this.busy) return;
        this.lock(true);
        try { await this.refresh(); } catch { /* Keep server-rendered count on transient failure. */ }
        finally { this.lock(false); }
      };
      document.addEventListener('click', this.onTrigger);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-cart-close]')) this.close();
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) this.change(remove.dataset.cartRemove, 0);
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-priority-toggle]')) this.togglePriority(event.target.checked);
        if (event.target.matches('[data-cart-quantity]')) this.change(event.target.dataset.cartQuantity, Number(event.target.value));
      });
      // Checkout reacts on the first tap: it waits for any cart update in flight, then goes straight to /checkout.
      this.querySelector('.tc-cart-checkout')?.form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = this.querySelector('.tc-cart-checkout');
        if (button.classList.contains('is-loading')) return;
        button.classList.add('is-loading');
        await this.idle;
        if (!this.cart.item_count) { button.classList.remove('is-loading'); return; }
        window.location.href = `${this.root}checkout`;
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('tc-cart-open');
        document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-expanded', 'false'));
      });
      const rail = this.querySelector('.tc-cart-recommendation-grid');
      rail.addEventListener('scroll', () => this.updateRecommendationArrows(), {passive:true});
      this.querySelectorAll('[data-recommendation-direction]').forEach(button => button.addEventListener('click', () => {
        const card = rail.firstElementChild;
        if (!card) return;
        rail.scrollBy({left:Number(button.dataset.recommendationDirection) * (card.getBoundingClientRect().width + 8), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      }));
      this.recommendationResize = new ResizeObserver(() => this.updateRecommendationArrows());
      this.recommendationResize.observe(rail);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
      this.recommendationResize?.disconnect();
      clearTimeout(this.closeTimer);
      clearInterval(this.reservationTimer);
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('tc-cart-open');
    }
    money(cents) {
      const currency = typeof this.cart.currency === 'string' ? this.cart.currency : this.cart.currency?.iso_code || 'USD';
      return new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency', currency}).format(cents / 100);
    }
    status(text, isError = false) {
      const status = this.querySelector('.tc-cart-status');
      status.classList.toggle('is-error', isError && Boolean(text));
      status.textContent = text;
    }
    lock(value) {
      this.busy = value;
      if (value) this.idle = new Promise(resolve => { this.releaseIdle = resolve; });
      else this.releaseIdle?.();
      this.dialog.setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-cart-quantity],[data-cart-remove],[data-priority-toggle]').forEach(node => node.disabled = value);
    }
    async refresh() {
      const response = await fetch(`${this.root}cart.js`, {headers:{Accept:'application/json'}, cache:'no-store'});
      if (!response.ok) throw new Error('Unable to load your cart. Please try again or view your full cart.');
      this.cart = await response.json();
      await this.ensureDefaultPriority();
      this.render();
      if (this.cartPageNeedsReload && !this.dialog.open) window.location.reload();
    }
    async ensureDefaultPriority() {
      const key = 'bootylift:priority-processing:v1';
      const items = this.cart.items || [];
      const products = items.filter(item => !this.isPriority(item));
      let preference;
      try { preference = sessionStorage.getItem(key); } catch { return; }
      if (!products.length) {
        if (preference === 'on') sessionStorage.removeItem(key);
        return;
      }
      if (items.some(item => this.isPriority(item))) {
        if (preference === null) sessionStorage.setItem(key,'on');
        return;
      }
      if (preference !== null) return;
      const variant = this.priorityProduct?.variants?.find(item => item.available);
      if (!variant) return;
      const response = await fetch(`${this.root}cart/add.js`, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1}]})});
      if (!response.ok) return;
      sessionStorage.setItem(key,'on');
      this.cartPageNeedsReload = this.dataset.cartPage === 'true';
      const refreshed = await fetch(`${this.root}cart.js`, {headers:{Accept:'application/json'},cache:'no-store'});
      if (!refreshed.ok) throw new Error('Unable to load your updated cart. Please try again.');
      this.cart = await refreshed.json();
    }
    // Priority Processing rides along with the first add, so it costs no extra round trip.
    defaultPriorityLines() {
      let preference;
      try { preference = sessionStorage.getItem('bootylift:priority-processing:v1'); } catch { return []; }
      if (preference !== null || (this.cart.items || []).some(item => this.isPriority(item))) return [];
      const variant = this.priorityProduct?.variants?.find(item => item.available);
      return variant ? [{id:variant.id, quantity:1}] : [];
    }
    // The cart as it will look after the add, built from product data already on the page.
    optimisticCart(lines) {
      const pdpProduct = document.querySelector('bl-product [data-product]');
      const products = [pdpProduct ? JSON.parse(pdpProduct.textContent) : this.recommendationProduct, this.priorityProduct].filter(Boolean);
      const items = (this.cart.items || []).map(item => ({...item}));
      for (const {id, quantity} of lines) {
        const product = products.find(entry => entry.variants?.some(variant => Number(variant.id) === Number(id)));
        const variant = product?.variants.find(entry => Number(entry.id) === Number(id));
        if (!variant) return null;
        const existing = items.find(item => Number(item.variant_id) === Number(id));
        if (existing) {
          existing.quantity += quantity;
          existing.original_line_price = existing.final_line_price = existing.price * existing.quantity;
          continue;
        }
        items.unshift({
          key:`pending-${id}`, variant_id:variant.id, product_id:product.id, handle:product.handle,
          product_title:product.title, variant_title:variant.title, url:`${this.root}products/${product.handle}?variant=${variant.id}`,
          image:variant.featured_image?.src || product.featured_image || '', quantity, price:variant.price,
          original_line_price:variant.price * quantity, final_line_price:variant.price * quantity, properties:{},
          options_with_values:(product.options || []).map((name, index) => ({name:name.name || name, value:variant.options[index]}))
        });
      }
      const total = items.reduce((sum, item) => sum + item.final_line_price, 0);
      return {...this.cart, items, item_count:items.reduce((sum, item) => sum + item.quantity, 0), original_total_price:total, items_subtotal_price:total, total_price:total};
    }
    // Opens the drawer with the new lines straight away; Shopify confirms them in the background.
    async add(lines) {
      const all = [...lines, ...this.defaultPriorityLines()];
      const previous = this.cart;
      const optimistic = this.optimisticCart(all);
      if (optimistic) {
        this.cart = optimistic; this.render(); this.status('');
        this.prefetchedAt = Date.now(); await this.open();
        this.lock(true);
      }
      try {
        const response = await fetch(`${this.root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({items:all})});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add this fit. Please try again.');
        if (all.length > lines.length) { try { sessionStorage.setItem('bootylift:priority-processing:v1','on'); } catch { /* Cart already holds it. */ } }
        await this.refresh();
        if (!optimistic) { this.prefetchedAt = Date.now(); await this.open(); }
      } catch (error) {
        this.cart = previous; this.render();
        if (optimistic) this.close();
        throw error;
      } finally { this.lock(false); }
    }
    // Load the cart before the drawer is shown, so an add never opens onto the page-load (empty) cart.
    async prefetch() {
      if (this.busy) return;
      this.lock(true);
      try { await this.refresh(); this.status(''); this.prefetchedAt = Date.now(); }
      catch { /* open() retries and reports the error. */ }
      finally { this.lock(false); }
    }
    async open() {
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      if (!this.dialog.open) {
        this.returnFocus = document.activeElement;
        this.dialog.showModal();
        document.body.classList.add('tc-cart-open');
        document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-expanded','true'));
        this.querySelector('[data-cart-close]').focus({preventScroll:true});
      }
      if (this.busy) return;
      if (Date.now() - (this.prefetchedAt || 0) < 2000) { this.prefetchedAt = 0; return; }
      this.lock(true); this.status('Updating cart…');
      try { await this.refresh(); this.status(''); }
      catch (error) { this.status(error.message, true); }
      finally { this.lock(false); }
    }
    close() {
      if (!this.dialog.open) return;
      this.dialog.classList.add('is-closing');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.dialog.close();
        this.dialog.classList.remove('is-closing');
        this.returnFocus?.focus({preventScroll:true});
        if (this.cartPageNeedsReload) window.location.reload();
      }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    }
    async change(key, quantity) {
      if (this.busy || !Number.isInteger(quantity) || quantity < 0) return;
      this.lock(true); this.status('Updating cart…');
      try {
        const products = (this.cart.items || []).filter(item => !this.isPriority(item));
        const removeLastProduct = quantity === 0 && products.length === 1 && products[0].key === key;
        const updates = removeLastProduct ? Object.fromEntries((this.cart.items || []).map(item => [item.key, 0])) : null;
        const response = await fetch(`${this.root}cart/${removeLastProduct ? 'update' : 'change'}.js`, {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify(updates ? {updates} : {id:key, quantity})});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to update this item.');
        this.cartPageNeedsReload = this.dataset.cartPage === 'true';
        this.cart = result; this.render(); this.status(quantity ? 'Quantity updated.' : 'Item removed.');
        const nextControl = [...this.querySelectorAll('[data-cart-quantity]')].find(node => node.dataset.cartQuantity === key);
        (nextControl || this.querySelector('.tc-cart-title')).focus({preventScroll:true});
      } catch (error) {
        // Reconcile quantities after a rejected inventory update.
        try { await this.refresh(); } catch { /* Leave the last confirmed cart visible. */ }
        this.status(error.message || 'Unable to update your cart. Please try again.', true);
      } finally { this.lock(false); }
    }
    comparisonLinePrice(item) {
      const pdpProduct = document.querySelector('bl-product [data-product]');
      const product = pdpProduct ? JSON.parse(pdpProduct.textContent) : this.recommendationProduct;
      const variant = product?.variants?.find(variant => Number(variant.id) === Number(item.variant_id));
      return Math.max(item.original_line_price || 0, item.final_line_price || 0, (variant?.compare_at_price || 0) * item.quantity);
    }
    render() {
      const allItems = this.cart.items || [];
      const items = allItems.filter(item => !this.isPriority(item));
      const priorityItems = allItems.filter(item => this.isPriority(item));
      const count = items.reduce((sum,item) => sum + item.quantity, 0);
      document.querySelectorAll('[data-cart-badge]').forEach(badge => { badge.textContent = count > 99 ? '99+' : String(count); badge.hidden = count === 0; });
      document.querySelectorAll('[data-cart-open]').forEach(trigger => trigger.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`));
      this.querySelector('[data-drawer-count]').textContent = String(count);
      this.querySelector('.tc-cart-empty').hidden = items.length !== 0;
      this.querySelector('.tc-cart-footer').hidden = allItems.length === 0;
      this.querySelector('.tc-cart-footer form').hidden = items.length === 0;
      this.renderPriority(priorityItems, items.length > 0);
      const list = this.querySelector('[data-cart-lines]'); list.replaceChildren();
      items.forEach(item => {
        const row = element('article','tc-cart-line');
        const picture = element('a','tc-cart-image'); picture.href = item.url;
        const lineColor = (item.options_with_values || []).find(option => /^colou?r$/i.test(option.name))?.value; const linePhoto = Number(item.product_id) === Number(this.recommendationProduct?.id) && this.linePhotos[lineColor]; const image = element('img'); image.src = linePhoto || item.image || item.featured_image?.url || ''; image.alt = item.product_title; image.loading = 'lazy';
        if (image.getAttribute('src')) picture.append(image);
        const content = element('div','tc-cart-line-content');
        const title = element('a','tc-cart-line-title',item.product_title); title.href = item.url;
        const fit = element('p','tc-cart-line-fit',(item.options_with_values || []).filter(option => option.value !== 'Default Title').map(option => {
          const value = option.value.replace(/(\d)Plus/g,'$1 Plus');
          if (/^length$/i.test(option.name)) return (value.match(/\(([^)]+)\)/) || [null,value])[1];
          return /^size$/i.test(option.name) ? `Size ${value}` : value;
        }).join(' · '));
        const controls = element('div','tc-cart-controls');
        const select = element('select'); select.dataset.cartQuantity = item.key; select.setAttribute('aria-label', `Quantity for ${item.product_title} ${item.variant_title || ''}`);
        for (let i=1; i<=Math.max(20,item.quantity); i++) { const option=element('option','',String(i)); option.value=String(i); option.selected=i===item.quantity; select.append(option); }
        const remove=element('button','tc-cart-remove','Remove'); remove.type='button'; remove.dataset.cartRemove=item.key; remove.setAttribute('aria-label',`Remove ${item.product_title} ${item.variant_title || ''}`);
        controls.append(select,remove);
        const prices=element('div','tc-cart-line-price');
        const comparison = this.comparisonLinePrice(item);
        if (comparison>item.final_line_price) { prices.append(element('del','',this.money(comparison))); prices.classList.add('is-sale'); }
        prices.append(element('span','',this.money(item.final_line_price)));
        content.append(title,fit,controls,prices);
        const properties=Object.entries(item.properties || {}).filter(([key,value])=>!key.startsWith('_')&&value);
        if (properties.length) content.append(element('p','tc-cart-properties',properties.map(([key,value])=>`${key}: ${value}`).join(' · ')));
        if (item.selling_plan_allocation?.selling_plan?.name) content.append(element('p','tc-cart-properties',item.selling_plan_allocation.selling_plan.name));
        (item.line_level_discount_allocations || []).forEach(discount=>content.append(element('p','tc-cart-discount',discount.discount_application.title)));
        row.append(picture,content); list.append(row);
      });
      const total = this.cart.total_price || 0;
      this.querySelector('[data-cart-total]').textContent=this.money(total);
      const compareTotal = allItems.reduce((sum,item)=>sum+this.comparisonLinePrice(item),0);
      const totalCompare = this.querySelector('[data-cart-compare]');
      totalCompare.hidden=compareTotal<=total;
      totalCompare.textContent=this.money(compareTotal);
      this.querySelector('.tc-cart-total-prices').classList.toggle('is-sale',compareTotal>total);
      const breakdown=this.querySelector('.tc-cart-breakdown'); breakdown.replaceChildren();
      const subtotal=element('p'); subtotal.append(element('span','','Items subtotal'),element('span','',this.money((this.cart.original_total_price || 0) - priorityItems.reduce((sum,item) => sum + item.original_line_price, 0)))); breakdown.append(subtotal);
      if (priorityItems.length) { const fee=element('p'); fee.append(element('span','','Priority Processing'),element('span','',this.money(priorityItems.reduce((sum,item) => sum + item.original_line_price, 0)))); breakdown.append(fee); }
      if (this.cart.total_discount>0) { const discount=element('p'); discount.append(element('span','','Discounts'),element('span','',`−${this.money(this.cart.total_discount)}`)); breakdown.append(discount); }
      this.renderRecommendations(items);
      this.updateReservation(count);
      this.updateShipping();
      if (!count) {
        try { if (sessionStorage.getItem('bootylift:priority-processing:v1') === 'on') sessionStorage.removeItem('bootylift:priority-processing:v1'); } catch { /* Optional preference storage. */ }
      }
    }
    updateShipping() {
      const bar = this.querySelector('[data-shipping-progress]');
      if (!bar) return;
      const pairs = (this.cart.items || []).filter(item => String(item.product_id) === this.dataset.shippingProduct).reduce((sum, item) => sum + item.quantity, 0);
      bar.hidden = pairs === 0;
      bar.classList.toggle('is-unlocked', pairs >= 2);
      bar.querySelector('[data-shipping-copy]').textContent = pairs >= 2 ? 'Free Shipping unlocked.' : 'Add 1 more pair to get Free Shipping';
      bar.querySelector('progress').value = Math.min(pairs, 2);
    }
    isPriority(item) {
      return item.handle === 'priority-processing' || this.priorityProduct?.variants?.some(variant => Number(variant.id) === Number(item.variant_id));
    }
    renderPriority(lines, hasProducts) {
      const variant = this.priorityProduct?.variants?.find(item => item.available);
      const row = this.querySelector('.tc-cart-priority');
      row.hidden = !lines.length && (!hasProducts || !variant);
      const toggle = this.querySelector('[data-priority-toggle]');
      toggle.checked = lines.length > 0;
      const amount = lines.length ? lines.reduce((sum,item) => sum + item.final_line_price, 0) : variant?.price || 0;
      this.querySelector('[data-priority-price]').textContent = `+${this.money(amount)}`;
      toggle.setAttribute('aria-label', `Priority Processing, ${this.money(amount)}`);
    }
    async togglePriority(enabled) {
      if (this.busy) return;
      const existing = (this.cart.items || []).filter(item => this.isPriority(item));
      const variant = this.priorityProduct?.variants?.find(item => item.available);
      if (enabled && (!variant || existing.length)) { this.render(); return; }
      this.lock(true); this.status('Updating Priority Processing…');
      try {
        const updates = Object.fromEntries(existing.map(item => [item.key, 0]));
        const response = await fetch(`${this.root}cart/${enabled ? 'add' : 'update'}.js`, {
          method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},
          body:JSON.stringify(enabled ? {items:[{id:variant.id,quantity:1}]} : {updates})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to update Priority Processing.');
        try { sessionStorage.setItem('bootylift:priority-processing:v1',enabled ? 'on' : 'off'); } catch { /* Cart state still reflects the choice. */ }
        this.cartPageNeedsReload = this.dataset.cartPage === 'true';
        await this.refresh();
        this.status(enabled ? 'Priority Processing added.' : 'Priority Processing removed.');
      } catch (error) {
        try { await this.refresh(); } catch { this.render(); }
        this.status(error.message || 'Unable to update Priority Processing. Please try again.', true);
      } finally { this.lock(false); }
    }
    updateReservation(count) {
      clearInterval(this.reservationTimer);
      const row = this.querySelector('.tc-cart-reservation');
      if (this.hasAttribute('data-shipping-product')) { row.hidden = true; return; }
      // Merchant confirmed backend reservations. Prefer its deadline when provided.
      const key = 'bootylift:cart-reservation:10m:v1';
      if (!count) {
        try { sessionStorage.removeItem(key); } catch { /* Storage may be unavailable. */ }
        row.hidden = true;
        return;
      }
      const raw = this.cart.attributes?.bootylift_reserved_until || this.dataset.reservationDeadline;
      let deadline;
      if (raw) {
        deadline = typeof raw === 'number' ? raw : Date.parse(raw);
      } else {
        try {
          const saved = sessionStorage.getItem(key);
          deadline = saved === null ? Date.now() + 10 * 60 * 1000 : Number(saved);
          if (saved === null) sessionStorage.setItem(key, String(deadline));
        } catch { deadline = NaN; }
      }
      if (!Number.isFinite(deadline)) { row.hidden = true; return; }
      row.hidden = false;
      const tick = () => {
        const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        row.classList.toggle('is-expired', seconds === 0);
        this.querySelector('[data-cart-reservation-active]').hidden = seconds === 0;
        this.querySelector('[data-cart-reservation-expired]').hidden = seconds > 0;
        this.querySelector('[data-cart-reservation-time]').textContent = `${String(Math.floor(seconds / 60)).padStart(2,'0')}:${String(seconds % 60).padStart(2,'0')}`;
        if (!seconds) clearInterval(this.reservationTimer);
      };
      tick();
      if (deadline > Date.now()) this.reservationTimer = setInterval(tick,1000);
    }
    updateRecommendationArrows() {
      const rail = this.querySelector('.tc-cart-recommendation-grid');
      this.querySelector('[data-recommendation-direction="-1"]').disabled = rail.scrollLeft <= 1;
      this.querySelector('[data-recommendation-direction="1"]').disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
    }
    sameFitVariant(product, item, card) {
      const names=(product.options || []).map(option=>typeof option==='string'?option:option.name);
      const values=names.map((name,index)=>index===Number(card.option)-1?card.color:(item.options_with_values || []).find(option=>option.name===name)?.value);
      return product.variants.find(variant=>variant.available && variant.options.every((value,index)=>value===values[index])) || null;
    }
    renderRecommendations(items) {
      const section=this.querySelector('.tc-cart-recommendations'), grid=this.querySelector('.tc-cart-recommendation-grid');
      grid.replaceChildren();
      const pdp=document.querySelector('bl-product');
      const product = this.recommendationProduct;
      const cards=this.recommendationCards.filter(card=>product?.variants?.some(v=>v.available && v.options[card.option-1]===card.color) && !items.some(item=>Number(item.product_id)===Number(product.id) && (item.options_with_values || []).some(option=>/^colou?r$/i.test(option.name)&&option.value===card.color)));
      section.hidden=items.length===0 || !cards.length;
      cards.forEach(card=>{
        const button=element('button','tc-cart-recommendation'); button.type='button'; button.setAttribute('aria-label',`Choose ${card.color} jeans`);
        const image=element('img'); image.src=card.image; image.alt=`${card.color} jeans`; image.loading='lazy';
        const media=element('span','tc-cart-recommendation-media');
        const plus=element('span','tc-cart-recommendation-plus'); plus.setAttribute('aria-hidden','true');
        media.append(image,plus);
        if (card.color === 'Brown' || card.color === 'White') media.append(element('span','tc-cart-recommendation-new','NEW'));
        const copy=element('span','tc-cart-recommendation-copy');
        copy.append(element('b','',card.color));
        const variants=product.variants.filter(variant=>variant.options[Number(card.option)-1]===card.color && variant.available);
        const cheapest=variants.reduce((best,variant)=>!best || variant.price<best.price?variant:best,null);
        if (cheapest) {
          const prices=element('span','tc-cart-recommendation-prices');
          if (cheapest.compare_at_price>cheapest.price) prices.append(element('del','',this.money(cheapest.compare_at_price)));
          prices.append(element('strong','',`${variants.some(variant=>variant.price!==cheapest.price)?'From ':''}${this.money(cheapest.price)}`));
          copy.append(prices);
        }
        copy.append(element('span','tc-cart-recommendation-fit','+ Add'));
        button.append(media,copy);
        // The same jeans are already in the cart: add this colour right away in that pair's size and length.
        const reference=items.find(item=>Number(item.product_id)===Number(product.id));
        const sameFit=reference && this.sameFitVariant(product, reference, card);
        if (sameFit) button.setAttribute('aria-label',`Add ${card.color} jeans in the same size`);
        button.addEventListener('click',async()=>{
          if (sameFit) {
            if (this.busy) return;
            try { await this.add([{id:sameFit.id, quantity:1}]); }
            catch (error) { this.status(error.message, true); }
            return;
          }
          this.returnFocus=null;
          this.close();
          if (!pdp || Number(pdp.product?.id) !== Number(product.id)) {
            const url = new URL(`${this.root}products/${product.handle}`, location.origin);
            url.searchParams.set('color', card.color);
            window.location.assign(window.blOfferURL?.(url) || url);
            return;
          }
          const option=pdp.querySelector(`.color-rail [data-value="${CSS.escape(card.color)}"]`);
          option.click();
          const fit=pdp.querySelector('.fit-options');
          const first=pdp.querySelector('.sizes .choice');
          setTimeout(()=>{ first.focus({preventScroll:true}); fit.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'center'}); },230);
        });
        grid.append(button);
      });
      grid.scrollLeft=0;
      requestAnimationFrame(()=>this.updateRecommendationArrows());
    }
  }
  if (!customElements.get('bl-pdp-cart')) customElements.define('bl-pdp-cart',BootyliftCart);
})();
